import { getAccessToken, getUser, login as authLogin, register as authRegister, logout as authLogout, me as authMe, bootstrapAuth, refresh as authRefresh, getApiUrl } from './auth-client.js';
import { createCaseRealtimeClient } from './case-realtime.js';

const VERSION_PREFIX = 'mindplace:sync-version:';
const BINDING_PREFIX = 'mindplace:sync-binding:';

function apiUrl() { return getApiUrl(); }
function authHeaders() { const token = getAccessToken(); return token ? { Authorization: `Bearer ${token}` } : {}; }
function emit(detail = {}) { try { window.dispatchEvent(new CustomEvent('mindplace:sync-state', { detail })); } catch (_) {} }
function isOnline() { return typeof navigator === 'undefined' || navigator.onLine !== false; }
function scopeId() { return getUser()?.id ? `user:${getUser().id}` : 'guest'; }
function versionKey(userId, caseId) { return `${VERSION_PREFIX}${userId || 'guest'}:${caseId}`; }
function bindingKey(caseId) { return `${BINDING_PREFIX}${scopeId()}:${caseId}`; }
function getVersion(userId, caseId) { try { return Number(localStorage.getItem(versionKey(userId, caseId)) || 0); } catch (_) { return 0; } }
function setVersion(userId, caseId, version) { try { localStorage.setItem(versionKey(userId, caseId), String(version)); } catch (_) {} }
function getBinding(caseId) { try { return localStorage.getItem(bindingKey(caseId)) || ''; } catch (_) { return ''; } }
function setBinding(caseId, userId) { try { if (userId) localStorage.setItem(bindingKey(caseId), userId); } catch (_) {} }

function publicState(lastError = '', conflict = null, extra = {}) {
  return { authenticated: Boolean(getAccessToken() && getUser()), user: getUser(), apiUrl: apiUrl(), online: isOnline(), scope: scopeId(), conflict, lastError, ...extra };
}

async function request(path, options = {}, retry = true) {
  let response;
  try {
    response = await fetch(`${apiUrl()}${path}`, {
      ...options,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(options.headers || {}) },
    });
  } catch (error) {
    const networkError = new Error('No se pudo conectar con el servidor. Los cambios permanecerán en IndexedDB.');
    networkError.cause = error;
    networkError.status = 0;
    throw networkError;
  }
  let body = null;
  try { body = await response.json(); } catch (_) {}
  if (response.status === 401 && retry && getAccessToken()) {
    const refreshed = await authRefresh(false);
    if (refreshed) return request(path, options, false);
  }
  if (!response.ok) {
    const error = new Error(body?.message || body?.error?.message || `Error HTTP ${response.status}`);
    error.status = response.status; error.body = body; throw error;
  }
  return body;
}

export function createSyncClient({ getStoreBridge = () => window.__mindplaceSyncStore, onState = () => {} } = {}) {
  let lastError = ''; let conflict = null; let conflictRecordId = ''; let destroyed = false;
  const realtime = createCaseRealtimeClient({ onEvent: handleRealtimeEvent, onState: snapshot => publish({ realtime: snapshot }) });
  const publish = (extra = {}) => { if (destroyed) return; const value = { ...publicState(lastError, conflict), ...extra }; try { onState(value); } catch (_) {} emit(value); return value; };

  async function handleRealtimeEvent(event){
    if(!event || !event.caseId) return;
    if(event.type === 'case.changed'){
      const remote = event.data || {};
      const currentUser = getUser();
      const localVersion = currentUser ? getVersion(currentUser.id,event.caseId) : 0;
      if(Number(remote.version) <= Number(localVersion)) return;
      const pending = await getStoreBridge()?.offline?.listPending?.() || [];
      const hasPending = pending.some(row => String(row.caseId || row.payload?.caseId || '') === String(event.caseId));
      if(hasPending){
        publish({ realtimeRemoteVersion:Number(remote.version), realtimeRemoteAvailable:true, remoteCaseId:event.caseId });
        return;
      }
      if(remote.case){
        const applied = await applyServerCase(remote.case);
        if(applied && currentUser){
          setVersion(currentUser.id,event.caseId,Number(remote.version));
          setBinding(event.caseId,currentUser.id);
          publish({ realtimeAppliedCaseId:event.caseId,realtimeVersion:Number(remote.version),realtimeRemoteAvailable:false });
        }
      } else {
        await pullAll();
      }
      return;
    }
    if(event.type === 'presence.snapshot') { publish({ realtimePresence:event.data?.presence || [] }); return; }
    if(event.type === 'presence.updated') { publish({ realtimePresenceEvent:event.data?.presence || null }); return; }
    if(event.type === 'presence.left') { publish({ realtimePresenceLeft:event.data?.userId || null }); return; }
    if(event.type === 'collaboration.access_revoked' && event.data?.userId === getUser()?.id) { realtime.stop(); lastError = 'Se ha revocado tu acceso a este caso.'; publish({ realtimeAccessRevoked:true }); return; }
    if(event.type === 'collaboration.permission_changed' && event.data?.userId === getUser()?.id) { publish({ realtimePermission:event.data.permission }); }
    if(event.type === 'activity' || event.type === 'ownership.transferred' || event.type === 'collaboration.permission_changed' || event.type === 'collaboration.access_revoked') { publish({ realtimeActivity:event }); }
  }

  async function login(identifier, password) { if (!isOnline()) throw new Error('No hay conexión con el servidor para iniciar sesión.'); const body = await authLogin(identifier, password); lastError = ''; conflict = null; conflictRecordId = ''; publish(); await bootstrapAuth(); await restorePersistedConflict(); await syncNow(); const current = getStoreBridge()?.getCurrentCaseId?.(); if(current) realtime.start(current); return body; }
  async function register(username, email, password, name = '') { if (!isOnline()) throw new Error('No hay conexión con el servidor para crear la cuenta.'); const body = await authRegister(username, email, password, name); lastError = ''; conflict = null; publish(); const current = getStoreBridge()?.getCurrentCaseId?.(); if(current) realtime.start(current); return body; }
  async function logout() { realtime.stop(); await authLogout(); lastError = ''; conflict = null; publish(); }
  async function me() { return authMe(); }
  async function listCases() { return (await request('/cases')).cases || []; }

  async function pushChange(payload) {
    const currentUser = getUser();
    if (!currentUser || !getAccessToken()) return { ok: false, error: 'No hay una sesión iniciada en el servidor.' };
    const caseId = String(payload?.caseId || payload?.payload?.caseId || '');
    const store = payload?.store || payload?.payload?.store;
    const caseData = store?.cases?.[caseId];
    if (!caseId || !caseData) return { ok: false, error: 'No se pudo determinar el caso a sincronizar.' };
    const binding = getBinding(caseId);
    if (binding && binding !== currentUser.id) return { ok: false, error: 'Este caso local está vinculado a otra cuenta. No se enviará automáticamente.' };
    setBinding(caseId, currentUser.id);
    const baseVersion = getVersion(currentUser.id, caseId);
    try {
      const body = await request('/sync/push', { method: 'POST', body: JSON.stringify({ caseId, baseVersion, caseData, clientUpdatedAt: caseData.updatedAt || Date.now() }) });
      const serverCase = body.case;
      await applyServerCase(serverCase);
      setVersion(currentUser.id, caseId, serverCase.version); setBinding(caseId, currentUser.id);
      lastError = ''; conflict = null; publish({ lastSyncedCaseId: caseId });
      return { ok: true, serverCase };
    } catch (error) {
      if (error.status === 409 && error.body?.code === 'SYNC_CONFLICT') {
        conflict = { caseId, baseVersion, localCase: caseData, serverCase: error.body.server, detectedAt: Date.now() };
        try { const savedId = await getStoreBridge()?.offline?.saveConflict?.({ queueId: null, caseId, result: { conflict }, localPayload: payload, createdAt: Date.now() }); if (savedId) conflictRecordId = savedId; } catch (_) {}
        lastError = 'Conflicto de sincronización detectado.'; publish({ conflict }); return { ok: false, conflict: true, error: lastError };
      }
      lastError = error?.message || 'Error de sincronización.'; publish(); return { ok: false, error: lastError };
    }
  }

  async function applyServerCase(serverCase) {
    if (!serverCase?.id) return false;
    const bridge = getStoreBridge();
    if (typeof bridge?.applyRemoteCase === 'function') return Boolean(await bridge.applyRemoteCase(serverCase.data, serverCase.version));
    return false;
  }

  async function pullAll() {
    const currentUser = getUser(); if (!currentUser || !getAccessToken()) return { ok: false, reason: 'unauthenticated' };
    try {
      const summaries = await listCases();
      const changedIds = summaries.filter(summary => getVersion(currentUser.id, summary.id) < Number(summary.version)).map(summary => summary.id);
      if (changedIds.length) {
        const pulled = await request('/sync/pull', { method: 'POST', body: JSON.stringify({ caseIds: changedIds }) });
        for (const remote of pulled.cases || []) {
          await applyServerCase(remote);
          setVersion(currentUser.id, remote.id, remote.version);
          setBinding(remote.id, currentUser.id);
        }
      }
      lastError = ''; publish({ pulled: changedIds.length, availableRemoteCases: summaries.length });
      return { ok: true, count: changedIds.length };
    } catch (error) { lastError = error?.message || 'No se pudieron descargar los casos.'; publish(); return { ok: false, error: lastError }; }
  }

  async function restorePersistedConflict() {
    try {
      if (conflict || !getUser() || !getAccessToken()) return Boolean(conflict);
      const records = await getStoreBridge()?.offline?.listConflicts?.();
      const record = Array.isArray(records) ? records[0] : null;
      const savedConflict = record?.result?.conflict || record?.conflict;
      if (!savedConflict?.caseId || !savedConflict?.serverCase) return false;
      conflict = savedConflict;
      conflictRecordId = record.id || '';
      lastError = 'Hay un conflicto de sincronización pendiente de resolución.';
      publish({ conflictRestored: true });
      return true;
    } catch (_) {
      return false;
    }
  }

  async function resolveConflict(strategy = 'server') {
    if (!conflict || !getUser()) return { ok: false, error: 'No hay conflicto activo.' };
    try {
      const localCase = conflict.localCase; const currentUser = getUser();
      const body = await request(`/cases/${encodeURIComponent(conflict.caseId)}/resolve`, { method: 'POST', body: JSON.stringify({ baseVersion: conflict.serverCase.version, strategy, caseData: strategy === 'client' ? localCase : undefined }) });
      const serverCase = body.case;
      await applyServerCase(serverCase);
      setVersion(currentUser.id, conflict.caseId, serverCase.version); setBinding(conflict.caseId, currentUser.id); conflict = null; lastError = '';
      if (conflictRecordId) { try { await getStoreBridge()?.offline?.clearConflict?.(conflictRecordId); } catch (_) {} conflictRecordId = ''; }
      publish(); return { ok: true, serverCase };
    } catch (error) { lastError = error?.message || 'No se pudo resolver el conflicto.'; publish(); return { ok: false, error: lastError }; }
  }

  async function queueOnlineSync(payload) {
    const currentUser = getUser();
    if (!currentUser || !getAccessToken()) return { ok: false, reason: 'unauthenticated' };
    const offline = getStoreBridge()?.offline;
    if (!offline?.enqueueStoreChange) return { ok: false, reason: 'offline-manager-unavailable' };
    const queued = await offline.enqueueStoreChange(payload?.reason || 'online-save');
    if (!queued) return { ok: false, reason: 'queue-failed' };
    return offline.flushQueue();
  }

  async function syncNow() {
    if (!getAccessToken()) return { ok: false, reason: 'unauthenticated' };
    if (!isOnline()) return { ok: false, reason: 'offline' };
    const queued = await getStoreBridge()?.offline?.flushQueue?.();
    await restorePersistedConflict();
    const pulled = await pullAll();
    return { ok: Boolean(pulled.ok) && (queued?.ok !== false), queued, pulled };
  }

  function setApiUrl(value) { try { localStorage.setItem('mindplace:sync-api-url', String(value || '/api').replace(/\/$/, '') || '/api'); } catch (_) {} publish({ apiUrl: apiUrl() }); }
  const onOnline = () => { publish({ online: true }); if (getAccessToken()) { void syncNow(); const current = getStoreBridge()?.getCurrentCaseId?.(); if (current) realtime.start(current); } };
  const onOffline = () => { publish({ online: false }); realtime.stop(false); };
  const onCaseChanged = (event) => { const id = String(event?.detail?.caseId || ''); if(id && getAccessToken() && isOnline()) realtime.start(id); else realtime.stop(!id); };
  window.addEventListener('online', onOnline); window.addEventListener('offline', onOffline); window.addEventListener('mindplace:current-case-changed', onCaseChanged);
  const bootRealtime = () => { const current = getStoreBridge()?.getCurrentCaseId?.(); if(current && getAccessToken() && isOnline()) realtime.start(current); };
  void bootstrapAuth().then(async () => { await restorePersistedConflict(); publish(); bootRealtime(); });
  return { login, register, logout, me, listCases, pushChange, pullAll, resolveConflict, restorePersistedConflict, queueOnlineSync, syncNow, setApiUrl, getApiUrl: apiUrl, getToken: getAccessToken, getUser, realtime, getState: () => publicState(lastError, conflict, { realtime: realtime.getState() }), destroy() { destroyed = true; realtime.destroy(); window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); window.removeEventListener('mindplace:current-case-changed', onCaseChanged); } };
}
